import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '@tark/shared/components';
import {
  MapPin,
  Database,
  RefreshCw,
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { getEnvironment } from '../../lib/cognito-config';

// ============================================
// Types
// ============================================

interface ZipMasterFileInfo {
  key: string;
  fileName: string;
  size: number;
  lastModified: string;
  country: string;
}

interface ZipMasterFilesResponse {
  bucket: string;
  prefix: string;
  environment: string;
  files: ZipMasterFileInfo[];
  totalFiles: number;
}

interface ZipMasterSeedResponse {
  success: boolean;
  message: string;
  dryRun: boolean;
  totalRows: number;
  processedRows: number;
  upsertedRows: number;
  skippedRows: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  sourceKey: string;
  sourceBucket: string;
  environment: string;
  errors: string[];
  totalErrorCount: number;
}

interface StatusResponse {
  seedInProgress: boolean;
  totalRecords: number;
  usRecords: number;
  caRecords: number;
}

// ============================================
// API
// ============================================

const API_BASE = '/api/v1/admin/reference/zip-master';

async function api<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ============================================
// Utilities
// ============================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

// ============================================
// Component
// ============================================

export function ZipMasterSeedPage() {
  const environment = getEnvironment();

  // State
  const [country, setCountry] = useState<'us' | 'ca'>('us');
  const [files, setFiles] = useState<ZipMasterFileInfo[]>([]);
  const [selectedFile, setSelectedFile] = useState<ZipMasterFileInfo | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [bucket, setBucket] = useState<string>('');
  const [seedResult, setSeedResult] = useState<ZipMasterSeedResponse | null>(null);

  // Loading states
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadBucket();
    loadStatus();
  }, []);

  // Load files when country changes
  useEffect(() => {
    loadFiles();
  }, [country]);

  async function loadBucket() {
    try {
      const data = await api<Record<string, string>>(`${API_BASE}/buckets`);
      setBucket(data[environment] || '');
    } catch (err) {
      console.error('Failed to load bucket:', err);
    }
  }

  async function loadStatus() {
    setLoadingStatus(true);
    try {
      const data = await api<StatusResponse>(`${API_BASE}/status`);
      setStatus(data);
    } catch (err) {
      console.error('Failed to load status:', err);
    } finally {
      setLoadingStatus(false);
    }
  }

  async function loadFiles() {
    setLoadingFiles(true);
    setError(null);
    setFiles([]);
    setSelectedFile(null);

    try {
      const data = await api<ZipMasterFilesResponse>(
        `${API_BASE}/files?env=${environment}&country=${country}`
      );
      setFiles(data.files);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoadingFiles(false);
    }
  }

  async function handleSeed(dryRun: boolean) {
    if (!selectedFile) return;

    dryRun ? setDryRunning(true) : setSeeding(true);
    setError(null);
    setSeedResult(null);

    try {
      const data = await api<ZipMasterSeedResponse>(`${API_BASE}/seed`, {
        method: 'POST',
        body: JSON.stringify({
          env: environment,
          key: selectedFile.key,
          mode: 'UPSERT',
          dryRun,
          batchSize: 500,
          lockTimeoutSeconds: 60,
        }),
      });

      setSeedResult(data);

      if (data.success && !dryRun) {
        loadStatus();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Seed operation failed');
    } finally {
      setSeeding(false);
      setDryRunning(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <MapPin className="h-8 w-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">ZIP Master</h1>
              <p className="text-slate-400">Manage ZIP code reference data</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Records"
            value={status?.totalRecords}
            loading={loadingStatus}
          />
          <StatCard
            label="US ZIP Codes"
            value={status?.usRecords}
            loading={loadingStatus}
            color="text-blue-400"
          />
          <StatCard
            label="CA Postal Codes"
            value={status?.caRecords}
            loading={loadingStatus}
            color="text-red-400"
          />
          <StatCard
            label="Status"
            value={status?.seedInProgress ? 'Seeding...' : 'Idle'}
            loading={loadingStatus}
            color={status?.seedInProgress ? 'text-yellow-400' : 'text-green-400'}
            isText
          />
        </div>

        {/* Source Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Source Files</CardTitle>
            <CardDescription>
              S3 Bucket: <code className="text-slate-300">{bucket || '...'}</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Country Filter */}
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm text-slate-400">Country:</label>
              <div className="relative">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value as 'us' | 'ca')}
                  className="bg-slate-800 border border-slate-700 rounded-lg pl-3 pr-8 py-2 text-white text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="us">United States</option>
                  <option value="ca">Canada</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadFiles}
                disabled={loadingFiles}
              >
                {loadingFiles ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <XCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Files List */}
            {loadingFiles ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No CSV files found
              </div>
            ) : (
              <div className="space-y-2">
                {files.map((file) => (
                  <FileRow
                    key={file.key}
                    file={file}
                    selected={selectedFile?.key === file.key}
                    onClick={() => setSelectedFile(file)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Seed Actions */}
        {selectedFile && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-400" />
                Seed Database
              </CardTitle>
              <CardDescription>
                File: <code className="text-slate-300">{selectedFile.fileName}</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleSeed(true)}
                  disabled={dryRunning || seeding}
                >
                  {dryRunning ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-2" />
                  )}
                  Dry Run
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleSeed(false)}
                  disabled={dryRunning || seeding}
                >
                  {seeding ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Seed
                </Button>
              </div>

              {environment === 'prod' && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>Warning: Production database</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {seedResult && <SeedResultCard result={seedResult} />}
    </div>
  );
}

// ============================================
// Sub-components
// ============================================

function StatCard({
  label,
  value,
  loading,
  color = 'text-white',
  isText = false,
}: {
  label: string;
  value?: number | string;
  loading: boolean;
  color?: string;
  isText?: boolean;
}) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500 mx-auto" />
      ) : (
        <>
          <div className={`text-2xl font-bold ${color}`}>
            {isText ? value : (value as number)?.toLocaleString() ?? '-'}
          </div>
          <div className="text-xs text-slate-500 mt-1">{label}</div>
        </>
      )}
    </div>
  );
}

function FileRow({
  file,
  selected,
  onClick,
}: {
  file: ZipMasterFileInfo;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between ${
        selected
          ? 'bg-blue-500/20 border border-blue-500/50'
          : 'bg-slate-800/50 border border-transparent hover:bg-slate-800'
      }`}
    >
      <div className="flex items-center gap-3">
        <FileText className={`h-5 w-5 ${selected ? 'text-blue-400' : 'text-slate-500'}`} />
        <div>
          <div className="font-medium text-white text-sm">{file.fileName}</div>
          <div className="text-xs text-slate-500">
            {formatBytes(file.size)} • {formatDate(file.lastModified)}
          </div>
        </div>
      </div>
      {selected && <CheckCircle2 className="h-5 w-5 text-blue-400" />}
    </div>
  );
}

function SeedResultCard({ result }: { result: ZipMasterSeedResponse }) {
  const success = result.success;

  return (
    <Card className={success ? '' : 'border-red-500/30'}>
      <CardHeader>
        <CardTitle className={`flex items-center gap-2 ${success ? 'text-green-400' : 'text-red-400'}`}>
          {success ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <XCircle className="h-5 w-5" />
          )}
          {success ? 'Completed' : 'Failed'}
          {result.dryRun && (
            <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">
              DRY RUN
            </span>
          )}
        </CardTitle>
        <CardDescription>{result.message}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <ResultStat label="Total" value={result.totalRows} />
          <ResultStat label="Processed" value={result.processedRows} color="text-blue-400" />
          <ResultStat label="Upserted" value={result.upsertedRows} color="text-green-400" />
          <ResultStat label="Skipped" value={result.skippedRows} color="text-yellow-400" />
        </div>

        <div className="text-sm text-slate-500">
          Duration: {(result.durationMs / 1000).toFixed(2)}s
        </div>

        {result.errors.length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium text-slate-400 mb-2">
              Errors ({result.totalErrorCount})
            </div>
            <div className="max-h-32 overflow-y-auto bg-slate-900 rounded-lg p-3 font-mono text-xs text-red-300">
              {result.errors.map((err, i) => (
                <div key={i} className="py-1 border-b border-slate-800 last:border-0">
                  {err}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResultStat({
  label,
  value,
  color = 'text-white',
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <div className="text-center p-3 bg-slate-800/50 rounded-lg">
      <div className={`text-xl font-bold ${color}`}>{value.toLocaleString()}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
